import React, { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Password input with a built-in show/hide eye toggle.
 *
 * Drop-in replacement for `<input type="password" />` — accepts every prop a
 * native input takes (value, onChange, placeholder, autoComplete, etc.) plus
 * an optional `eyeTestId` for Playwright targeting of the toggle button.
 *
 * The component owns the visibility state; it does not lift it to the parent
 * because nothing else on the page needs to know whether the password is
 * currently masked. The eye toggle is rendered absolutely positioned inside
 * the input row so the existing visual layout of the page is preserved — the
 * input still occupies its normal slot.
 *
 * For pages that style their own input (dark theme, custom border, etc.), the
 * `className` you pass is applied to the inner <input> exactly like before;
 * the wrapping <div> is just `relative` so the button can anchor to the right.
 */
const PasswordInput = forwardRef(function PasswordInput(
  {
    className = "",
    eyeClassName = "",
    eyeTestId,
    "data-testid": dataTestId,
    ...rest
  },
  ref,
) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? "text" : "password"}
        data-testid={dataTestId}
        // Reserve room on the right for the eye button so long passwords
        // don't collide with the icon.
        className={`${className} pr-10`}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        tabIndex={-1}
        aria-label={visible ? "Hide password" : "Show password"}
        data-testid={eyeTestId || (dataTestId ? `${dataTestId}-eye` : undefined)}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#7A7A7A] hover:text-[#1A422D] focus:text-[#1A422D] focus:outline-none ${eyeClassName}`}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
});

export default PasswordInput;
